import { useEffect, useRef, useState } from 'react';

export default function ImageSkeleton({
  src = '',
  alt = '',
  className = '',
  imgClassName = '',
  trackLoad = true,
  width,
  height,
  aspectRatio,
  radius = '2xl',
  logoSize = 72,
  logoClassName = '',
  onLoad,
  onError,
  style,
  ...props
}) {
  const imageStyle = { ...style };
  if (aspectRatio != null) imageStyle.aspectRatio = aspectRatio;
  if (width != null) imageStyle.width = typeof width === 'number' ? `${width}px` : width;
  if (height != null) imageStyle.height = typeof height === 'number' ? `${height}px` : height;
  const normalizedSrc = String(src || '').trim();
  const imgRef = useRef(null);
  const [loadedSrc, setLoadedSrc] = useState('');

  const radiusClass =
    radius === 'none'
      ? 'rounded-none'
      : radius === 'sm'
      ? 'rounded-sm'
      : radius === 'lg'
      ? 'rounded-lg'
      : radius === 'xl'
      ? 'rounded-xl'
      : radius === '2xl'
      ? 'rounded-2xl'
      : radius === 'full'
      ? 'rounded-full'
      : 'rounded-md';

  useEffect(() => {
    if (!trackLoad) return;
    if (!normalizedSrc) return;

    const img = imgRef.current;
    if (!img) return;

    // On refresh/hydration the browser may have already loaded the image from cache
    // before React attaches onLoad, so detect that state directly.
    if (img.complete && img.naturalWidth > 0) {
      setLoadedSrc(normalizedSrc);
    }
  }, [normalizedSrc, trackLoad]);

  if (!normalizedSrc && trackLoad) {
    return null;
  }

  const isLoaded = trackLoad ? loadedSrc === normalizedSrc : false;

  function handleLoad(event) {
    setLoadedSrc(normalizedSrc);
    if (typeof onLoad === 'function') onLoad(event);
  }

  function handleError(event) {
    if (typeof onError === 'function') onError(event);
  }

  return (
    <>
      <div
        className={`relative isolate overflow-hidden bg-[var(--dash-black)] ${radiusClass} ${className}`.trim()}
        style={imageStyle}
        {...props}
      >
        {!isLoaded ? (
          <div aria-hidden="true" className="absolute inset-0 z-20">
            <div className="absolute inset-0 image-skeleton-base" />
            <div className="image-skeleton-shimmer absolute inset-y-0 left-[-45%] w-[45%]" />
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/logos/logo.svg"
              alt=""
              className={`image-skeleton-logo pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 grayscale ${logoClassName}`.trim()}
              style={{
                width: typeof logoSize === 'number' ? `${logoSize}px` : logoSize,
                height: 'auto',
              }}
            />
          </div>
        ) : null}
        {trackLoad ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            ref={imgRef}
            src={normalizedSrc}
            alt={alt}
            onLoad={handleLoad}
            onError={handleError}
            className={`relative z-10 h-full w-full transition-opacity duration-500 ${
              isLoaded ? 'opacity-100' : 'opacity-0'
            } ${imgClassName}`.trim()}
          />
        ) : null}
      </div>
      <style jsx>{`
        .image-skeleton-base {
          background:
            radial-gradient(circle at 18% 18%, rgba(var(--dash-main-rgb), 0.18), transparent 48%),
            linear-gradient(145deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.01));
        }

        .image-skeleton-shimmer {
          background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(255, 255, 255, 0.28) 50%,
            transparent 100%
          );
          animation: image-skeleton-shimmer 1.5s ease-in-out infinite;
        }

        .image-skeleton-logo {
          opacity: 0.34;
          filter: grayscale(1) brightness(1.15);
          animation: image-skeleton-logo 2s ease-in-out infinite;
        }

        @keyframes image-skeleton-shimmer {
          0% {
            transform: translateX(0);
            opacity: 0;
          }

          20% {
            opacity: 1;
          }

          100% {
            transform: translateX(350%);
            opacity: 0;
          }
        }

        @keyframes image-skeleton-logo {
          0%,
          100% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 0.22;
          }

          50% {
            transform: translate(-50%, -50%) scale(1.04);
            opacity: 0.5;
          }
        }
      `}</style>
    </>
  );
}
